import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ViewNav } from "@/components/ViewNav";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { MapPin, Loader2, Ruler, DoorOpen, AlertTriangle, Wrench, Sparkles } from "lucide-react";
import { Brand } from "@/components/Brand";

const GroupCard = ({ group, onSizeJump, prices = {} }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`availability-group-${group.name.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-slate-700" />
        <h2 className="font-display font-bold text-xl text-slate-900">{group.name}</h2>
      </div>
      <div className="flex flex-wrap items-center gap-5 text-right">
        <div>
          <p className="font-display text-2xl font-bold text-green-600 leading-none font-mono-nums">{group.total_available}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">available</p>
        </div>
        {group.total_attention > 0 && (
          <div>
            <p className="font-display text-2xl font-bold text-amber-600 leading-none font-mono-nums">{group.total_attention}</p>
            <p className="text-xs text-amber-600 uppercase tracking-wider">attention</p>
          </div>
        )}
        <div>
          <p className="font-display text-2xl font-bold text-slate-900 leading-none font-mono-nums">{group.total_units}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">total</p>
        </div>
      </div>
    </div>

    {group.sizes.length === 0 ? (
      <p className="px-5 py-8 text-center text-slate-400 italic">No units in this area.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/60">
              <th className="text-left font-medium px-5 py-2.5">Size</th>
              <th className="text-left font-medium px-2 py-2.5 text-emerald-700">Price</th>
              <th className="text-center font-medium px-2 py-2.5 text-green-600">Available</th>
              <th className="text-center font-medium px-2 py-2.5 text-amber-600">Needs Attention</th>
              <th className="text-center font-medium px-2 py-2.5 text-blue-600">Occupied</th>
              <th className="text-center font-medium px-5 py-2.5 text-slate-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {group.sizes.map((s) => {
              const testBase = `${group.name.toLowerCase().replace(/\s+/g, "-")}-${s.size}`;
              return (
                <tr
                  key={s.size}
                  data-testid={`availability-size-${testBase}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
                >
                  <td className="px-5 py-3">
                    <button
                      type="button"
                      data-testid={`size-jump-${testBase}`}
                      onClick={() => onSizeJump(s.size)}
                      className="flex items-center gap-1.5 font-semibold text-slate-700 hover:text-blue-600 hover:underline transition-colors"
                      title="View these sheds on the dashboard"
                    >
                      <Ruler className="h-3.5 w-3.5 text-slate-400" />
                      {s.size === "unspecified" ? "No size" : s.size}
                    </button>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap" data-testid={`size-price-${testBase}`}>
                    {prices[s.size] ? (
                      <span className="font-mono-nums text-xs text-slate-700">
                        <b className="text-slate-900">${prices[s.size].weekly}</b>/wk <span className="text-slate-400">·</span> <b className="text-slate-900">${prices[s.size].monthly}</b>/mo
                      </span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td className="text-center px-2 py-3 font-mono-nums font-bold text-lg text-green-600" data-testid={`available-count-${testBase}`}>
                    {s.available || "—"}
                  </td>
                  <td className="text-center px-2 py-3">
                    {s.attention > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid={`attention-count-${testBase}`}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 font-mono-nums font-bold text-sm cursor-pointer hover:bg-amber-200 hover:border-amber-400 transition-colors"
                          >
                            <AlertTriangle className="h-3 w-3" /> {s.attention}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="center" className="w-80 p-0 overflow-hidden" data-testid={`attention-popover-${testBase}`}>
                          <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200">
                            <p className="font-semibold text-amber-800 text-sm flex items-center gap-1.5">
                              <AlertTriangle className="h-4 w-4" /> {s.attention} of {s.available} need attention
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">
                              {s.size === "unspecified" ? "No size" : s.size} · {s.needs_cleaning} cleaning · {s.maintenance} maintenance
                            </p>
                          </div>
                          <div className="max-h-64 overflow-auto divide-y divide-slate-100 text-left">
                            {s.attention_units.map((u) => {
                              const Icon = u.status === "maintenance" ? Wrench : Sparkles;
                              const reason = u.tasks && u.tasks.length > 0
                                ? u.tasks.join(", ")
                                : (u.notes || (u.status === "maintenance" ? "Maintenance required before move-in" : "Cleaning required before move-in"));
                              return (
                                <div key={u.unit_number} className="px-4 py-2.5" data-testid={`attention-unit-${u.unit_number}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono-nums font-bold text-slate-900 text-sm flex items-center gap-1.5">
                                      <Icon className="h-3.5 w-3.5 text-amber-600" /> {u.unit_number}
                                    </span>
                                    <StatusBadge status={u.status} />
                                  </div>
                                  <p className="text-xs text-slate-600 mt-1 leading-snug">{reason}</p>
                                </div>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="text-center px-2 py-3 font-mono-nums text-slate-500">{s.occupied || "—"}</td>
                  <td className="text-center px-5 py-3 font-mono-nums font-semibold text-slate-900">{s.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function AvailabilitySummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    api.getAvailability().then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    api.getPricing().then((p) => {
      setPrices(Object.fromEntries((p.size_rates || []).map((r) => [r.size.toLowerCase().replace(/\s/g, ""), r])));
    }).catch(() => {});
  }, []);

  const handleSizeJump = (size) => navigate(`/units?size=${encodeURIComponent(size)}&status=available`);

  const totalAvailable = data ? data.groups.reduce((a, g) => a + g.total_available, 0) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="377 units · 3 sites" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DoorOpen className="h-6 w-6 text-green-600" /> Availability Summary
            </h2>
            <p className="text-sm text-slate-500 mt-1">Available inventory by location group and size. Vacant, maintenance and cleaning units all count as available — amber flags need attention before move-in.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-right shadow-sm">
            <p className="font-display text-2xl font-bold text-slate-900 font-mono-nums leading-none" data-testid="availability-total">{totalAvailable}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">total available</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading availability...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="availability-grid">
            {data.groups.map((g) => <GroupCard key={g.name} group={g} onSizeJump={handleSizeJump} prices={prices} />)}
          </div>
        )}
      </main>
    </div>
  );
}
